import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Clock, Users, Info, StopCircle, AlertTriangle, RefreshCw, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppRenewal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ sent: 0, failed: 0, total: 0 });
  const stopSendingRef = useRef(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    template_sid: localStorage.getItem('wa_renewal_template_sid') || '',
    magazine: 'Aabkari Times',
    target_type: 'expired',
  });

  // Persist template_sid in localStorage
  useEffect(() => {
    if (formData.template_sid) {
      localStorage.setItem('wa_renewal_template_sid', formData.template_sid);
    }
  }, [formData.template_sid]);

  const [testData, setTestData] = useState({
    test_phone: '',
    sample_name: 'Test Member',
    sample_member_id: 'TEST001',
    sample_expiry: 'March 2026',
  });

  const handleSendTest = async () => {
    if (!formData.template_sid) {
      toast.error('Please enter the Template Content SID first');
      return;
    }
    if (!testData.test_phone) {
      toast.error('Please enter a test phone number');
      return;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/send-renewal-test`, {
        template_sid: formData.template_sid,
        magazine: formData.magazine,
        ...testData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Test message sent!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setTestSending(false);
    }
  };

  // Preview recipients
  const handlePreviewRecipients = async () => {
    if (!formData.template_sid) {
      toast.error('Please enter the Template Content SID');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/renewal-preview`, {
        magazine: formData.magazine,
        target_type: formData.target_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRecipientCount(response.data.count);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get recipient count');
    } finally {
      setLoading(false);
    }
  };

  const handleStopSending = () => {
    stopSendingRef.current = true;
    toast.warning('Stopping... Will complete after current batch');
  };

  const handleConfirmSend = async () => {
    setShowConfirmation(false);
    stopSendingRef.current = false;
    setLoading(true);
    setSendingProgress({ sent: 0, failed: 0, total: recipientCount });
    setResults(null);
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/whatsapp/send-renewal-reminder`, {
        template_sid: formData.template_sid,
        magazine: formData.magazine,
        target_type: formData.target_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSendingProgress({ 
        sent: response.data.success_count, 
        failed: response.data.failed_count, 
        total: response.data.success_count + response.data.failed_count 
      });
      setResults(response.data);
      
      toast.success(`Completed! Sent to ${response.data.success_count} recipients`);
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send messages');
    } finally {
      setLoading(false);
      stopSendingRef.current = false;
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center space-x-4 mb-8">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#f59e0b' }} data-testid="page-title">
              <Clock className="inline mr-3" size={36} />
              WhatsApp Renewal Reminder
            </h1>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <Info className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
              <div>
                <p className="text-sm text-blue-700">
                  <strong>Template Variables:</strong>
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li><code className="bg-blue-100 px-1 rounded">{'{{1}}'}</code> = Expiry Month Year (e.g., "February 2026")</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{2}}'}</code> = Member ID (e.g., "ATMR1234")</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{3}}'}</code> = Member Name</li>
                </ul>
              </div>
            </div>
          </div>

          {/* WhatsApp Number Indicator */}
          <div className={`border p-4 rounded-lg mb-6 ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Phone className={`mr-3 ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'text-purple-600' : 'text-green-600'}`} size={24} />
                <div>
                  <p className={`font-semibold ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'text-purple-700' : 'text-green-700'}`}>
                    Sending from: {formData.magazine}
                  </p>
                  <p className={`text-sm ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'text-purple-600' : 'text-green-600'}`}>
                    WhatsApp Number: <strong>{formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG' ? '+1 762 770 0003' : '+1 551 365 3462'}</strong>
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${(formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? 'bg-purple-200 text-purple-700' : 'bg-green-200 text-green-700'}`}>
                {formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG' ? 'AT' : 'ST'}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-6" style={{ color: '#f59e0b' }}>
              Send Renewal Reminders
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Template SID */}
              <div className="space-y-2 md:col-span-2">
                <Label>Template Content SID *</Label>
                <Input
                  value={formData.template_sid}
                  onChange={(e) => setFormData({ ...formData, template_sid: e.target.value })}
                  placeholder="e.g., HXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                  data-testid="template-sid-input"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">Get this from Twilio Console → Messaging → Content Editor</p>
              </div>

              {/* Magazine */}
              <div className="space-y-2">
                <Label>Magazine *</Label>
                <Select 
                  value={formData.magazine} 
                  onValueChange={(v) => setFormData({ ...formData, magazine: v })}
                  disabled={loading}
                >
                  <SelectTrigger data-testid="magazine-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                    <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Target Type */}
              <div className="space-y-2">
                <Label>Send To *</Label>
                <Select 
                  value={formData.target_type} 
                  onValueChange={(v) => setFormData({ ...formData, target_type: v })}
                  disabled={loading}
                >
                  <SelectTrigger data-testid="target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expired">
                      <div className="flex items-center">
                        <AlertTriangle size={16} className="mr-2 text-red-500" />
                        Expired Members
                      </div>
                    </SelectItem>
                    <SelectItem value="expiring_soon">
                      <div className="flex items-center">
                        <Clock size={16} className="mr-2 text-orange-500" />
                        Expiring in 3 Months
                      </div>
                    </SelectItem>
                    <SelectItem value="both">
                      <div className="flex items-center">
                        <Users size={16} className="mr-2 text-blue-500" />
                        Both Expired & Expiring
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Progress Bar */}
            {loading && sendingProgress.total > 0 && (
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-orange-700">Sending in progress...</span>
                  <span className="text-sm text-orange-600">
                    {sendingProgress.sent + sendingProgress.failed} / {sendingProgress.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-orange-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${((sendingProgress.sent + sendingProgress.failed) / sendingProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Test Message Section */}
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-700">
                <Send className="mr-2" size={18} />
                Send Test Message First
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Test Phone Number *</Label>
                    <Input
                      value={testData.test_phone}
                      onChange={(e) => setTestData({ ...testData, test_phone: e.target.value })}
                      placeholder="9876543210"
                      data-testid="test-phone-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Sample Name</Label>
                    <Input
                      value={testData.sample_name}
                      onChange={(e) => setTestData({ ...testData, sample_name: e.target.value })}
                      placeholder="Test Member"
                      data-testid="test-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Sample Member ID</Label>
                    <Input
                      value={testData.sample_member_id}
                      onChange={(e) => setTestData({ ...testData, sample_member_id: e.target.value })}
                      placeholder="TEST001"
                      data-testid="test-memberid-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Sample Expiry (Variable 1)</Label>
                    <Input
                      value={testData.sample_expiry}
                      onChange={(e) => setTestData({ ...testData, sample_expiry: e.target.value })}
                      placeholder="March 2026"
                      data-testid="test-expiry-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSendTest}
                  disabled={testSending || !formData.template_sid || !testData.test_phone}
                  variant="outline"
                  className="border-blue-400 text-blue-700 hover:bg-blue-100"
                  data-testid="send-test-btn"
                >
                  {testSending ? <><RefreshCw className="mr-2 animate-spin" size={16} /> Sending...</> : <><Send className="mr-2" size={16} /> Send Test Message</>}
                </Button>
                {testResult && (
                  <div className={`p-3 rounded text-sm ${testResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="test-result">
                    <p className="font-semibold">{testResult.success ? 'Test Sent!' : 'Test Failed'}</p>
                    <p>{testResult.message}</p>
                    {testResult.variables_used && (
                      <p className="mt-1 text-xs opacity-80">
                        Variables: {Object.entries(testResult.variables_used).map(([k,v]) => `${k}="${v}"`).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex gap-4">
              {!loading ? (
                <Button 
                  onClick={handlePreviewRecipients} 
                  disabled={loading}
                  className="flex-1 py-6 text-lg"
                  style={{ backgroundColor: '#f59e0b' }}
                  data-testid="send-button"
                >
                  <Send className="mr-2" size={20} />
                  Preview & Send
                </Button>
              ) : (
                <Button 
                  onClick={handleStopSending}
                  variant="destructive"
                  className="flex-1 py-6 text-lg"
                  data-testid="stop-button"
                >
                  <StopCircle className="mr-2" size={20} />
                  STOP SENDING
                </Button>
              )}
            </div>
          </div>

          {/* Confirmation Modal */}
          {showConfirmation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="text-orange-500 mr-3" size={32} />
                  <h2 className="text-xl font-bold">Confirm Send</h2>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg mb-4">
                  <p className="text-lg text-center">
                    You are about to send renewal reminders to
                  </p>
                  <p className="text-4xl font-bold text-center text-orange-600 my-2">
                    {recipientCount} members
                  </p>
                  <p className="text-sm text-center text-gray-600">
                    {formData.target_type === 'expired' ? 'Expired Members' : 
                     formData.target_type === 'expiring_soon' ? 'Expiring in 3 months' : 'All Expired & Expiring'}
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Each member will receive a personalized message with their expiry date, member ID, and name.
                </p>

                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowConfirmation(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleConfirmSend}
                    className="flex-1"
                    style={{ backgroundColor: '#f59e0b' }}
                  >
                    <Send className="mr-2" size={16} />
                    Yes, Send Now
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#f59e0b' }}>
                Broadcast Results
              </h2>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold">{results.success_count + results.failed_count}</p>
                  <p className="text-sm text-gray-600">Total Recipients</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{results.success_count}</p>
                  <p className="text-sm text-green-700">Sent Successfully</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{results.failed_count}</p>
                  <p className="text-sm text-red-700">Failed</p>
                </div>
              </div>

              {results.results && results.results.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-3 py-2 text-left">Member ID</th>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Expiry</th>
                        <th className="border px-3 py-2 text-left">Phone</th>
                        <th className="border px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.slice(0, 20).map((r, idx) => (
                        <tr key={idx} className={r.status === 'failed' ? 'bg-red-50' : ''}>
                          <td className="border px-3 py-2">{r.member_id}</td>
                          <td className="border px-3 py-2">{r.name}</td>
                          <td className="border px-3 py-2">{r.expiry_date}</td>
                          <td className="border px-3 py-2">{r.phone}</td>
                          <td className="border px-3 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              r.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {r.status === 'sent' ? '✅ Sent' : '❌ Failed'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {results.results.length > 20 && (
                    <p className="text-sm text-gray-500 mt-2">Showing first 20 of {results.results.length} results</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WhatsAppRenewal;
